import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail, Shield, Clock, CreditCard, Settings, Wrench, Bot } from 'lucide-react';
import SiteFooter from '../components/SiteFooter';

const SUPPORT_EMAIL = 'yovel@yovaiagency.com';

const SupportCard = ({ icon: Icon, num, title, children }) => (
  <div className="support-card" data-testid={`support-card-${num}`}>
    <div className="support-card-head">
      <span className="support-card-num">{String(num).padStart(2, '0')}</span>
      <span className="support-card-icon"><Icon className="w-5 h-5" /></span>
      <h2 className="support-card-title">{title}</h2>
    </div>
    <div className="support-card-body">{children}</div>
  </div>
);

const Support = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="landing-page">
      <div className="ambient-bg" />
      <div className="grain" />

      <nav className="site-nav legal-nav">
        <div className="site-nav-inner">
          <Link to="/" className="nav-link legal-back" data-testid="support-back-home">
            <ArrowLeft className="inline w-4 h-4 mr-1" /> Back To Home
          </Link>
        </div>
      </nav>

      <main className="legal-wrap">
        <div className="legal-inner support-inner">
          {/* Premium document-style header */}
          <header className="legal-doc-header">
            <img
              src="/yovai-logo.png"
              alt="YOVAI"
              className="legal-doc-logo"
              data-testid="support-doc-logo"
            />
            <div className="legal-entity">YOVAI LLC · CUSTOMER SUPPORT</div>
            <h1 className="legal-title" data-testid="support-title">YOVAI Customer Support</h1>
            <p className="support-subheading" data-testid="support-subheading">
              We're Here To Help With Your YOVAI Account, AI Services, Billing, Onboarding, And Technical Support.
            </p>
            <div className="legal-divider" />
          </header>

          {/* Intro */}
          <section className="support-intro">
            <p>
              Thank You For Choosing YOVAI. Our Team Is Available To Assist Current And Prospective Clients With Questions Regarding Their Account, Subscription, Billing, Implementation, Onboarding, Lead Recovery, Booking Growth, Multi-Location Operations, And Other YOVAI Services.
            </p>
          </section>

          {/* Support sections as cards */}
          <div className="support-grid">
            <SupportCard num={1} icon={Settings} title="Account & Service Support">
              <p>
                Clients Can Contact YOVAI For Assistance With Their Account, Service Configuration, AI Receptionist Setup, Business Information, Scheduling Integrations, Workflows, And General Service Questions.
              </p>
            </SupportCard>

            <SupportCard num={2} icon={CreditCard} title="Billing & Subscription Support">
              <p>
                For Questions Regarding Subscription Charges, One-Time Setup Fees, Setup Payment Milestones, Payment Methods, Billing Information, Invoices, Subscription Changes, Or Cancellations, Please Contact YOVAI Support.
              </p>
              <p className="support-note" data-testid="support-payment-note">Service Fees, Implementation Costs, Billing Schedules, And Ongoing Support Arrangements Are Defined In Each Client's Approved Proposal And Service Agreement.</p>
            </SupportCard>

            <SupportCard num={3} icon={Bot} title="Onboarding & Implementation">
              <p>
                After Purchasing A YOVAI Service, Clients Will Receive Instructions Regarding Onboarding And Implementation. This May Include Collecting Business Information, Configuring The AI Receptionist, Scheduling Setup, Call Handling Preferences, FAQs, Workflows, Integrations, And Testing Before Launch.
              </p>
            </SupportCard>

            <SupportCard num={4} icon={Wrench} title="Technical Support">
              <p>
                Clients Experiencing Technical Issues With Their YOVAI Service Should Contact Support And Include Their Business Name, Contact Information, And A Short Description Of The Issue.
              </p>
            </SupportCard>
          </div>

          {/* Contact CTA */}
          <section className="support-contact-hero" data-testid="support-contact-section">
            <span className="eyebrow"><span className="dot" />CONTACT YOVAI SUPPORT</span>
            <h2 className="support-contact-title">Send Us A Message</h2>
            <p className="support-contact-copy">
              Reach Our Support Team Directly. Include Your Business Name, Contact Information, And A Short Description Of Your Request So We Can Help As Quickly As Possible.
            </p>

            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=YOVAI%20Support%20Request`}
              className="btn-primary support-contact-btn"
              data-testid="support-contact-btn"
            >
              <Mail className="w-4 h-4" /> Contact YOVAI Support
            </a>

            <div className="support-contact-email">
              Or Email Us Directly:{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="support-email-link" data-testid="support-email-link">
                {SUPPORT_EMAIL}
              </a>
            </div>
          </section>

          {/* Response expectations + Security */}
          <div className="support-notice-grid">
            <div className="support-notice" data-testid="support-response-notice">
              <div className="support-notice-icon"><Clock className="w-5 h-5" /></div>
              <div>
                <h3 className="support-notice-title">Response Expectations</h3>
                <p>
                  Support Requests Are Reviewed As Promptly As Possible. Response Times May Vary Depending On The Nature Of The Request.
                </p>
              </div>
            </div>
            <div className="support-notice" data-testid="support-security-notice">
              <div className="support-notice-icon"><Shield className="w-5 h-5" /></div>
              <div>
                <h3 className="support-notice-title">Security Notice</h3>
                <p>
                  For Your Security, Please Do Not Send Passwords, Full Payment Card Numbers, Or Other Highly Sensitive Credentials Through Email Or The Support Form.
                </p>
              </div>
            </div>
          </div>

          {/* Legal links */}
          <div className="support-legal-links" data-testid="support-legal-links">
            <Link to="/terms" className="support-legal-link" data-testid="support-legal-terms">
              Terms Of Service <ArrowRight className="inline w-3.5 h-3.5" />
            </Link>
            <Link to="/privacy" className="support-legal-link" data-testid="support-legal-privacy">
              Privacy Policy <ArrowRight className="inline w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Official closing block */}
          <footer className="legal-doc-close">
            <div className="legal-divider legal-divider--close" />
            <div className="legal-doc-close-body">
              <div className="legal-doc-close-name">YOVAI LLC</div>
              <div>Georgia, United States</div>
              <div>
                <a href="https://www.yovaiagency.com">www.yovaiagency.com</a>
              </div>
            </div>
          </footer>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Support;
